import { useFormikContext } from 'formik'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { ROUTES } from 'src/constants'
import { usePrompt } from 'src/hooks'
import { IScanToEarnCreateForm } from 'src/types'
import { SCAN_TO_EARN_FORM_INITIAL } from '.'
import { HowDoesItWorksModal } from './how-it-works-modal.component'

export enum EScanToEarnProgressions {
  CAMERA = 'CAMERA',
  ADD_MANUALLY = 'ADD_MANUALLY',
  ADD_MEDIA = 'UPLOAD_MEDIA',
  DETAILS = 'DETAILS',
}

interface IContext {
  progression: EScanToEarnProgressions
  setProgression: Dispatch<SetStateAction<IContext['progression']>>
  isHowDoesItWorkOpen: boolean
  handleHowDoesItWorkClose: () => void
  handleHowDoesItWorkOpen: () => void
}

const ScanToEarnContext = createContext<IContext>({
  progression: EScanToEarnProgressions.CAMERA,
  setProgression: () => {},
  isHowDoesItWorkOpen: false,
  handleHowDoesItWorkClose: () => {},
  handleHowDoesItWorkOpen: () => {},
})

export const useScanToEarnContext = () => useContext(ScanToEarnContext)

interface IProps {
  children: ReactNode
  resetForm: () => void
}

export const ScanToEarnContextWrapper = ({ children, resetForm }: IProps) => {
  const [t] = useTranslation()
  const location = useLocation()

  const { values, isSubmitting } = useFormikContext<IScanToEarnCreateForm>()

  usePrompt({
    message: t('prompt-unsaved-changes.v'),
    when: values.barcode_value !== SCAN_TO_EARN_FORM_INITIAL.barcode_value && !isSubmitting,
    beforeUnload: true,
  })

  const [progression, setProgression] = useState<IContext['progression']>(
    EScanToEarnProgressions.CAMERA,
  )

  const [isHowDoesItWorkOpen, setIsHowDoesItWorkOpen] = useState(false)

  const handleHowDoesItWorkClose = () => {
    setIsHowDoesItWorkOpen(false)
  }

  const handleHowDoesItWorkOpen = () => {
    setIsHowDoesItWorkOpen(true)
  }

  useEffect(() => {
    if (location.pathname === `/${ROUTES.USER}/${ROUTES.SCAN_TO_EARN}`) {
      resetForm()
      setProgression(EScanToEarnProgressions.CAMERA)
    }
  }, [location])

  return (
    <ScanToEarnContext.Provider
      value={{
        progression,
        setProgression,
        isHowDoesItWorkOpen,
        handleHowDoesItWorkClose,
        handleHowDoesItWorkOpen,
      }}
    >
      {children}
      <HowDoesItWorksModal open={isHowDoesItWorkOpen} handleClose={handleHowDoesItWorkClose} />
    </ScanToEarnContext.Provider>
  )
}
